import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Theme,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
} from '@mui/material';
import { CloudDownload } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useGetForecastAnalyticsQuery,
  useGetAwardedAnalyticsQuery,
  useGetProjectTypesQuery,
  useGetSubscribersQuery,
  useGetTeamContactsQuery,
  useGetTeamsQuery,
  useLazyGetForecastAnalyticsExportQuery,
  useGetWinRateAnalyticsQuery,
} from 'store/api';
import { Contact, ProjectType, Subscriber, Team } from 'store/api/models';
import {
  JFormik,
  KpiCard,
  numToPercent,
  useBreakpointQuery,
} from '@centerpiece/components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { useAnalyticContext } from 'contexts/analytic.context';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const BidVolumeWinRateLineChart = () => {
  const {
    contactRecord,
    team,
    setTeam,
    individual,
    setIndividual,
    projectType,
    setProjectType,
  } = useAnalyticContext();
  const { data: winRate = [], isLoading: winRateLoading } =
    useGetWinRateAnalyticsQuery({
      params: {
        team: team?.id,
        individual: individual?.id,
      },
    });

  const chartData = useMemo(() => {
    const endDate = moment();
    const startDate = moment().subtract(12, 'months'); // one year in the past;
    const data: any = {};

    // Loop through each month from the start date to the current date
    let currentDate = moment(startDate);
    while (currentDate.isSameOrBefore(endDate)) {
      const yearMonth = currentDate.format('YYYY-MM');
      data[yearMonth] = {
        awarded_rev: 0,
        pre_award_rev: 0,
        unknown: 0,
      };

      // Move to the next month
      currentDate.add(1, 'month');
    }
    data['No Date'] = {
      awarded_rev: 0,
      pre_award_rev: 0,
      unknown: 0,
    };
    winRate?.forEach((f: any) => {
      const awarded_rev = f.awarded ? f.revenue_sum : 0;
      const pre_award_rev = f.awarded === false ? f.revenue_sum : 0;
      const unknown = f.awarded === null ? f.revenue_sum : 0;
      if (f.year) {
        const key = `${f?.year}-${f.month > 9 ? f.month : `0${f.month}`}`;
        data[key] = {
          awarded_rev: data[key]?.awarded_rev + awarded_rev,
          pre_award_rev: data[key]?.pre_award_rev + pre_award_rev,
          unknown: data[key]?.unknown + unknown,
        };
      } else {
        const key = 'No Date';
        data[key] = {
          awarded_rev: data[key]?.awarded_rev + awarded_rev,
          pre_award_rev: data[key]?.pre_award_rev + pre_award_rev,
          unknown: data[key]?.unknown + unknown,
        };
      }
    });
    return {
      labels: Object.keys(data),
      datasets: [
        {
          label: 'Revenue from awarded jobs',
          data: Object.values(data)?.map((d: any) => d.awarded_rev),
          backgroundColor: '#76B041',
        },
        {
          label: 'Expected revenue from quotes/bids',
          data: Object.values(data)?.map((d: any) => d.pre_award_rev),
          backgroundColor: '#163B3F',
        },
        {
          label: 'Revenue from projects with no stage',
          data: Object.values(data)?.map((d: any) => d.unknown),
          backgroundColor: 'gray',
        },
      ],
    };
  }, [winRate]);

  const mdDown = useBreakpointQuery('md-down');
  const smDown = useBreakpointQuery('sm-down');
  return (
    <Grid item xs={12}>
      <Paper elevation={5}>
        <Grid
          container
          alignItems="center"
          sx={{
            pl: { xs: 2, md: 4 },
            pr: { xs: 2, md: 4 },
            pt: 2,
            pb: 1,
          }}
        >
          <Typography variant="h6">Revenue Forecast</Typography>
          {/* {exportResult.isLoading ? (
              <CircularProgress size={16} />
            ) : (
              <IconButton
                onClick={() => {
                  exportData({
                    params: {
                      team: team?.id,
                      individual: individual?.id,
                    },
                  });
                }}
                sx={{ ml: 2 }}
                size="large"
              >
                <CloudDownload />
              </IconButton>
            )} */}
        </Grid>

        <Box sx={{ pl: { xs: 1, md: 3 }, pr: { xs: 2, md: 4 } }}>
          <Line
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top' as const,
                },
                title: {
                  display: true,
                  text: 'Chart.js Line Chart',
                },
              },
              scales: {
                y: {
                  type: 'linear',
                  display: true,
                  position: 'left',
                },
                y1: {
                  type: 'linear',
                  display: true,
                  position: 'right',

                  // grid line settings
                  grid: {
                    drawOnChartArea: false, // only want the grid lines for one axis to show up
                  },
                },
              },
            }}
            data={chartData}
            //   {
            //   labels,
            //   datasets: [
            //     {
            //       label: 'Trailing Bid Volume',
            //       data: labels.map(() =>
            //         faker.datatype.number({ min: -1000, max: 1000 })
            //       ),
            //       borderColor: 'rgb(255, 99, 132)',
            //       backgroundColor: 'rgba(255, 99, 132, 0.5)',
            //       yAxisID: 'y'
            //     },
            //     {
            //       label: 'Win Rate',
            //       data: labels.map(() =>
            //         faker.datatype.number({ min: -1000, max: 1000 })
            //       ),
            //       borderColor: 'rgb(53, 162, 235)',
            //       backgroundColor: 'rgba(53, 162, 235, 0.5)',
            //       yAxisID: 'y1',
            //     },
            //   ],
            // }}
          />
        </Box>

        <Grid
          container
          flexDirection={smDown ? 'column' : 'row'}
          alignItems={smDown ? 'flex-start' : 'center'}
          justifyContent="space-between"
          sx={{ pl: { xs: 6, md: 8 }, pr: { xs: 3, md: 5 }, pt: 2, pb: 2 }}
        >
          <Grid
            item
            flexDirection={mdDown ? 'column' : 'row'}
            alignItems={mdDown ? 'flex-start' : 'center'}
            sx={{ display: 'flex' }}
          >
            <Grid item sx={{ mr: 3, display: 'flex', alignItems: 'center' }}>
              <Box
                width={12}
                height={12}
                bgcolor={'#76B041'}
                marginRight={1}
              ></Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className="leading-none"
              >
                Revenue from awarded jobs
              </Typography>
            </Grid>

            <Grid
              item
              sx={{
                mr: 3,
                mt: mdDown ? 1 : 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                width={12}
                height={12}
                bgcolor={'#163B3F'}
                marginRight={1}
              ></Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className="leading-none"
              >
                Expected revenue from quotes/bids
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                mt: mdDown ? 1 : 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                width={12}
                height={12}
                bgcolor={'gray'}
                marginRight={1}
              ></Box>
              <Typography
                variant="body2"
                color="textSecondary"
                className="leading-none"
              >
                Revenue from projects with no stage
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};
